import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { usePrevious } from 'react-use';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { useQueryClient } from 'react-query';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import Capsule, { CAROUSEL } from '../CommonComponents/Capsule';
import PlanSelectionDropdown from '../PlanSelectionDropdown/PlanSelectionDropdownContainer';
import ComponentDictionary from './GoalCarouselComponentDictionary';
import { clearCarouselData } from './GoalCarouselActions';
import { queryKeys } from '../OverviewOPL/api/queryKeys';

const GoalCarouselView = props => {
  const {
    clientId,
    fpId,
    capsuleId,
    isInitialized,
    goalsList,
    proposedPlan,
    currentGoalIndex,
    currentGoalData,
    isCurrentPlan,
    initialChartData,
    goForward,
    goBackward,
    fetchData,
    isResettingFinancialPlan,
  } = props;

  const dispatch = useDispatch();
  const { id } = currentGoalData;
  const previousId = usePrevious(id);

  const queryClient = useQueryClient();

  const isInitialRequest = previousId === undefined;

  // TODO: check -> isResettingFinancialPlan
  useEffect(() => {
    if (isInitialRequest) {
      // Initial request
      fetchData(clientId, fpId);
    }

    return () => {
      dispatch(clearCarouselData());
    };
  }, [clientId, isResettingFinancialPlan]);

  useEffectOnce(() => {
    return () => {
      queryClient.removeQueries(queryKeys.goalChartAll());
    };
  });

  if (!isInitialized) {
    return null;
  }

  const Chart = ComponentDictionary[currentGoalData.component].chart;

  const canAccessFinancialPlan =
    ADV.util.env('isAdviserArea') ||
    get(ADV, 'data.user.ModulesToHide', []).indexOf('FinancialPlan') < 0;

  return (
    <div>
      <PlanSelectionDropdown
        capsuleId={capsuleId}
        clientId={clientId}
        className={!canAccessFinancialPlan && 'no-link'}
      />
      <Capsule
        header="Financial Plan"
        subheader={null}
        className={CAROUSEL}
        href={canAccessFinancialPlan && 'NewFinancialPlanWhatIfs'}
        params={{
          clientId,
          goalId: currentGoalData.id,
        }}
        fpId={fpId}
        clientId={clientId}
      >
        <div key={currentGoalData.id}>
          {goalsList.length > 1 && (
            <span
              role="button"
              tabIndex={0}
              onKeyDown={() => {
                // nothing
              }}
              onClick={goBackward(clientId, currentGoalIndex, goalsList.length)}
              className="backward toggle-slide"
            >
              <i className="fa fa-chevron-left" />
            </span>
          )}
          {goalsList.length > 1 && (
            <span
              role="button"
              tabIndex={0}
              onKeyDown={() => {
                // nothing
              }}
              onClick={goForward(clientId, currentGoalIndex, goalsList.length)}
              className="forward toggle-slide"
            >
              <i className="fa fa-chevron-right" />
            </span>
          )}
          <Chart
            isCurrentPlan={isCurrentPlan}
            key={currentGoalData.id}
            fpId={fpId}
            proposedPlan={proposedPlan}
            goalId={currentGoalData.id}
            initialChartData={initialChartData}
            clientId={clientId}
            title={currentGoalData.name}
          />
        </div>
      </Capsule>
    </div>
  );
};

GoalCarouselView.defaultProps = {
  initialChartData: null,
  currentGoalData: {
    id: null,
  },
};

GoalCarouselView.propTypes = {
  clientId: PropTypes.string.isRequired,
  fpId: PropTypes.string.isRequired,
  capsuleId: PropTypes.string.isRequired,
  isInitialized: PropTypes.bool.isRequired,
  currentGoalIndex: PropTypes.number.isRequired,
  currentGoalData: PropTypes.shape({
    component: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  proposedPlan: PropTypes.string.isRequired,
  goForward: PropTypes.func.isRequired,
  goBackward: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  goalsList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  initialChartData: PropTypes.shape({}),
  isCurrentPlan: PropTypes.bool.isRequired,
  isResettingFinancialPlan: PropTypes.bool.isRequired,
};

export default GoalCarouselView;
